/**
 * Pipedrive Organization Detail Page
 * Shows organization details with related people, deals, activities, and notes
 */

import { useParams, Link } from 'react-router-dom'
import { usePipedriveOrganization } from '@/hooks/use-pipedrive-api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Building2,
  Users,
  Briefcase,
  Calendar,
  StickyNote,
  ArrowLeft,
  Mail,
  Phone,
  Globe,
  MapPin,
  ExternalLink,
} from 'lucide-react'
import { CATEGORICAL, SEMANTIC } from '@/lib/theme'

export default function PipedriveOrganizationDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: org, isLoading, error } = usePipedriveOrganization(id ?? '')

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load organization. Please try again later.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    )
  }

  if (!org) {
    return (
      <div className="p-6">
        <div className="bg-muted p-4 rounded-lg">Organization not found.</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/pipedrive/organizations">
          <Button variant="ghost" size="icon" aria-label="Back to organizations">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" aria-hidden="true" />
            {org.name}
          </h1>
          <p className="text-muted-foreground">
            {org.industry || 'No industry'} {org.owner ? `• Owner: ${org.owner}` : ''}
          </p>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {org.email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a
                  href={`mailto:${org.email}`}
                  className="hover:underline hover:opacity-70"
                  style={{ color: CATEGORICAL.navy.solid }}
                >
                  {org.email}
                </a>
              </div>
            )}
            {org.phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <a
                  href={`tel:${org.phone}`}
                  className="hover:underline hover:opacity-70"
                  style={{ color: CATEGORICAL.navy.solid }}
                >
                  {org.phone}
                </a>
              </div>
            )}
            {org.website && (
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <a
                  href={org.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:opacity-70"
                  style={{ color: CATEGORICAL.navy.solid }}
                >
                  {org.website}
                </a>
              </div>
            )}
            {(org.address || org.city || org.state || org.country) && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  {org.address && <div>{org.address}</div>}
                  {(org.city || org.state || org.postal_code) && (
                    <div>{[org.city, org.state, org.postal_code].filter(Boolean).join(', ')}</div>
                  )}
                  {org.country && <div>{org.country}</div>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Open Deals</p>
                <p className="text-2xl font-bold" style={{ color: CATEGORICAL.navy.solid }}>
                  {org.open_deals_count ?? 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Won Deals</p>
                <p className="text-2xl font-bold" style={{ color: CATEGORICAL.evergreen.solid }}>
                  {org.won_deals_count ?? 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lost Deals</p>
                <p className="text-2xl font-bold" style={{ color: CATEGORICAL.raspberry.solid }}>
                  {org.lost_deals_count ?? 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Activities</p>
                <p className="text-2xl font-bold">{org.activities_count ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Related Records Tabs */}
      <Tabs defaultValue="people">
        <TabsList>
          <TabsTrigger value="people" className="flex items-center gap-2">
            <Users
              className="h-4 w-4"
              style={{ color: CATEGORICAL.teal.solid }}
              aria-hidden="true"
            />
            People ({org.people?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="deals" className="flex items-center gap-2">
            <Briefcase
              className="h-4 w-4"
              style={{ color: CATEGORICAL.clay.solid }}
              aria-hidden="true"
            />
            Deals ({org.deals?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex items-center gap-2">
            <Calendar
              className="h-4 w-4"
              style={{ color: CATEGORICAL.evergreen.solid }}
              aria-hidden="true"
            />
            Activities ({org.activities?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2">
            <StickyNote
              className="h-4 w-4"
              style={{ color: CATEGORICAL.plum.solid }}
              aria-hidden="true"
            />
            Notes ({org.notes?.length ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="people">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {org.people?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No people found
                      </TableCell>
                    </TableRow>
                  ) : (
                    org.people?.map((person) => (
                      <TableRow key={person.id}>
                        <TableCell>
                          <Link
                            to={`/pipedrive/people/${person.id}`}
                            className="font-medium hover:underline flex items-center gap-1 hover:opacity-70"
                            style={{ color: CATEGORICAL.navy.solid }}
                          >
                            {person.name}
                            <ExternalLink className="h-3 w-3" aria-hidden="true" />
                          </Link>
                        </TableCell>
                        <TableCell>{person.job_title || '-'}</TableCell>
                        <TableCell>{person.email_work || '-'}</TableCell>
                        <TableCell>{person.phone_work || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deals">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {org.deals?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No deals found
                      </TableCell>
                    </TableRow>
                  ) : (
                    org.deals?.map((deal) => (
                      <TableRow key={deal.id}>
                        <TableCell>
                          <Link
                            to={`/pipedrive/deals/${deal.id}`}
                            className="font-medium hover:underline flex items-center gap-1 hover:opacity-70"
                            style={{ color: CATEGORICAL.navy.solid }}
                          >
                            {deal.title}
                            <ExternalLink className="h-3 w-3" aria-hidden="true" />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <span
                            className="capitalize"
                            style={{
                              color:
                                deal.status === 'won'
                                  ? CATEGORICAL.evergreen.solid
                                  : deal.status === 'lost'
                                    ? CATEGORICAL.raspberry.solid
                                    : CATEGORICAL.navy.solid,
                            }}
                          >
                            {deal.status}
                          </span>
                        </TableCell>
                        <TableCell>{deal.stage_name || '-'}</TableCell>
                        <TableCell className="text-right">
                          {deal.value ? `$${deal.value.toLocaleString()}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Done</TableHead>
                    <TableHead>Due Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {org.activities?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No activities found
                      </TableCell>
                    </TableRow>
                  ) : (
                    org.activities?.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell className="font-medium">
                          <Link
                            to={`/pipedrive/activities/${activity.id}`}
                            className="hover:underline hover:opacity-70"
                            style={{ color: CATEGORICAL.navy.solid }}
                          >
                            {activity.subject || '-'}
                          </Link>
                        </TableCell>
                        <TableCell>{activity.type || '-'}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              color:
                                activity.done === 'Yes'
                                  ? SEMANTIC.success.text
                                  : SEMANTIC.warning.text,
                              backgroundColor:
                                activity.done === 'Yes'
                                  ? SEMANTIC.success.fill
                                  : SEMANTIC.warning.fill,
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                            }}
                          >
                            {activity.done}
                          </span>
                        </TableCell>
                        <TableCell>{activity.due_date || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Content</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {org.notes?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        No notes found
                      </TableCell>
                    </TableRow>
                  ) : (
                    org.notes?.map((note) => (
                      <TableRow key={note.id}>
                        <TableCell className="max-w-md">
                          <Link
                            to={`/pipedrive/notes/${note.id}`}
                            className="hover:underline truncate block hover:opacity-70"
                            style={{ color: CATEGORICAL.navy.solid }}
                          >
                            {note.content || '-'}
                          </Link>
                        </TableCell>
                        <TableCell>{note.user_name || '-'}</TableCell>
                        <TableCell>{note.add_time || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
